import { CobaltButton } from '@cae/cobalt-react/components/button';
import {
  CobaltFormField,
  CobaltFormFieldHelperText,
  CobaltFormFieldLabel,
} from '@cae/cobalt-react/components/form-field';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltLoaderCircular } from '@cae/cobalt-react/components/loader-circular';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltTextInput } from '@cae/cobalt-react/components/text-input';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import * as React from 'react';

import type { RegisterUserFormProps } from './RegisterUserForm.types';

import { useRegisterUserForm } from '../../hooks/use-register-user-form/useRegisterUserForm';
import styles from './RegisterUserForm.module.css';
import { isEmailConflictError } from './RegisterUserForm.utils';

const RegisterUserForm = React.memo(
  ({
    onSubmitSuccess,
    onEmailExists,
  }: Readonly<RegisterUserFormProps>): React.ReactElement => {
    const {
      firstName,
      middleName,
      lastName,
      email,
      validationErrors,
      touched,
      isPending,
      isError,
      error,
      setFirstName,
      setMiddleName,
      setLastName,
      setEmail,
      handleBlur,
      handleSubmit,
    } = useRegisterUserForm({ onSubmitSuccess, onEmailExists });

    const handleContactCareClick = React.useCallback((): void => {
      navigateToHelp(true);
    }, []);

    return (
      <div className={styles.formWrapper} data-testid="register-user-form">
        {isPending && (
          <div
            className={styles.loadingOverlay}
            aria-busy="true"
            aria-label="Creating your account"
            data-testid="register-user-form-loading-overlay"
          >
            <CobaltLoaderCircular size="large" />
            <p className={styles.loadingLabel}>Creating your account...</p>
          </div>
        )}
        <form
          className={styles.registerUserForm}
          onSubmit={handleSubmit}
          noValidate
        >
          <h2 className={styles.formHeading}>New Account</h2>

          <div className={styles.formFields}>
            {isError && !isEmailConflictError(error) && (
              <CobaltSectionMessage
                className={styles.sectionMessage}
                variant="danger"
                sectionMessageTitle={
                  <h3>
                    Request submission failed. Please try again or contact
                    customer care if the issue persists.
                  </h3>
                }
                sectionMessageAction={
                  <CobaltLink onClick={handleContactCareClick}>
                    Contact customer care
                  </CobaltLink>
                }
              />
            )}
            <CobaltFormField
              id="firstName"
              error={touched.firstName ? validationErrors.firstName : undefined}
            >
              <CobaltFormFieldLabel htmlFor="firstName" label="First name" />
              <CobaltTextInput
                id="firstName"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                onBlur={() => handleBlur('firstName')}
              />
              {touched.firstName && validationErrors.firstName && (
                <CobaltFormFieldHelperText
                  helperId="firstName-helper"
                  error={validationErrors.firstName}
                >
                  {validationErrors.firstName}
                </CobaltFormFieldHelperText>
              )}
            </CobaltFormField>

            <CobaltFormField id="middleName">
              <CobaltFormFieldLabel
                htmlFor="middleName"
                label="Middle name (Optional)"
              />
              <CobaltTextInput
                id="middleName"
                value={middleName}
                onChange={e => setMiddleName(e.target.value)}
              />
            </CobaltFormField>

            <CobaltFormField
              id="lastName"
              error={touched.lastName ? validationErrors.lastName : undefined}
            >
              <CobaltFormFieldLabel htmlFor="lastName" label="Last name" />
              <CobaltTextInput
                id="lastName"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                onBlur={() => handleBlur('lastName')}
              />
              {touched.lastName && validationErrors.lastName && (
                <CobaltFormFieldHelperText
                  helperId="lastName-helper"
                  error={validationErrors.lastName}
                >
                  {validationErrors.lastName}
                </CobaltFormFieldHelperText>
              )}
            </CobaltFormField>

            <CobaltFormField
              id="email"
              className={styles.emailField}
              error={touched.email ? validationErrors.email : undefined}
            >
              <CobaltFormFieldLabel htmlFor="email" label="Email" />
              <CobaltTextInput
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
              />
              {touched.email && validationErrors.email && (
                <CobaltFormFieldHelperText
                  helperId="email-helper"
                  error={validationErrors.email}
                >
                  {validationErrors.email}
                </CobaltFormFieldHelperText>
              )}
            </CobaltFormField>

            <CobaltButton
              className={styles.submitButton}
              type="submit"
              variant="primary"
              disabled={isPending}
              pending={isPending}
            >
              Next
            </CobaltButton>
          </div>
        </form>
      </div>
    );
  }
);

RegisterUserForm.displayName = 'RegisterUserForm';

export { RegisterUserForm };
